import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Calendar as CalendarIcon, Share2, Check } from "lucide-react";
import { motion } from "framer-motion";
import { getInsightBySlug, getRelatedInsights } from "@/lib/insights";
import { COMPANY } from "@/lib/content";

function ReadingProgress() {
  const [w, setW] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setW(total > 0 ? Math.min(100, (h.scrollTop / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-20 left-0 right-0 z-30 h-0.5 bg-transparent pointer-events-none">
      <div className="h-full bg-gold transition-[width] duration-100" style={{ width: `${w}%` }} />
    </div>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-charcoal mt-16 mb-4 tracking-tight leading-tight">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-heading text-lg md:text-xl font-medium text-charcoal mt-10 mb-3 tracking-tight">
          {block.text}
        </h3>
      );
    case "p":
      return <p className="text-charcoal-soft text-base md:text-lg leading-relaxed mt-4">{block.text}</p>;
    case "list":
      return (
        <ul className="mt-5 space-y-2">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 items-start text-charcoal">
              <Check className="w-4 h-4 text-gold shrink-0 mt-1.5" />
              <span className="leading-relaxed">{it}</span>
            </li>
          ))}
        </ul>
      );
    case "olist":
      return (
        <ol className="mt-5 space-y-3 counter-reset">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-4 items-start">
              <div className="font-mono text-xs uppercase tracking-[0.24em] text-gold pt-1 shrink-0 w-6 text-right">
                {String(i + 1).padStart(2, "0")}
              </div>
              <span className="text-charcoal leading-relaxed">{it}</span>
            </li>
          ))}
        </ol>
      );
    case "quote":
      return (
        <blockquote className="my-12 pl-6 border-l-2 border-gold">
          <p className="font-heading text-2xl md:text-3xl italic text-charcoal leading-snug">
            "{block.text}"
          </p>
        </blockquote>
      );
    case "image":
      return (
        <figure className="my-14 -mx-6 md:mx-0">
          <img src={block.src} alt={block.caption || ""} className="w-full aspect-[16/9] object-cover" />
          {block.caption && (
            <figcaption className="mt-3 text-xs font-mono uppercase tracking-[0.16em] text-charcoal-soft text-center px-6">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case "cta":
      return (
        <div className="my-14 bg-charcoal text-white p-8 md:p-12">
          <div className="overline text-gold mb-4">Groman Associates</div>
          <div className="font-heading text-2xl md:text-3xl font-semibold leading-tight">{block.title}</div>
          <p className="mt-4 text-white/75 leading-relaxed max-w-2xl">{block.body}</p>
          <Link
            to="/contact"
            data-testid="insight-inline-cta"
            className="mt-8 inline-flex items-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors"
          >
            {block.label} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    case "divider":
      return <div className="my-12 h-px bg-gray-200" />;
    default:
      return null;
  }
}

export default function InsightPage() {
  const { slug } = useParams();
  const article = getInsightBySlug(slug);
  if (!article) return <Navigate to="/" replace />;
  const related = getRelatedInsights(slug, 2);
  const pub = new Date(article.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: article.title, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); } catch {}
    }
  };

  return (
    <div data-testid="insight-page">
      <ReadingProgress />

      {/* Hero */}
      <section className="relative bg-charcoal text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={article.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/70 to-charcoal" />
        </div>
        <div className="grain-overlay" />
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <Link to="/#insights" data-testid="insight-back" className="inline-flex items-center gap-2 text-white/70 hover:text-gold text-sm font-mono uppercase tracking-[0.24em] transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> Back to insights
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold mb-6">
            {article.category} · {article.read}
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            {article.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/75 leading-relaxed max-w-2xl">
            {article.subtitle}
          </p>
          <div className="mt-10 flex items-center gap-8 text-xs font-mono uppercase tracking-[0.2em] text-white/50">
            <span className="flex items-center gap-2"><CalendarIcon className="w-3.5 h-3.5 text-gold" />{pub}</span>
            <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-gold" />{article.read}</span>
            <button data-testid="insight-share" onClick={share} className="flex items-center gap-2 hover:text-gold transition-colors">
              <Share2 className="w-3.5 h-3.5 text-gold" /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Article body */}
      <motion.article
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-[720px] mx-auto px-6 lg:px-0 pt-16 pb-24"
      >
        {article.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </motion.article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-offwhite border-t border-gray-200">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
            <div className="overline mb-4">Continue Reading</div>
            <h2 className="font-heading text-2xl md:text-4xl font-semibold text-charcoal tracking-tight mb-12">More from Groman Insights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/insights/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group border border-gray-200 bg-white hover:border-gold transition-colors block"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={r.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
                      {r.category} · {r.read}
                    </div>
                    <div className="mt-3 font-heading text-lg font-medium text-charcoal group-hover:text-gold transition-colors leading-snug">
                      {r.title}
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-charcoal">
                      Read insight <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="bg-charcoal text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="overline text-gold mb-3">Talk to {COMPANY.short}</div>
            <div className="font-heading text-2xl md:text-3xl">Every insight starts with one conversation.</div>
          </div>
          <Link to="/contact" data-testid="insight-cta-bottom" className="inline-flex items-center gap-2 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors">
            Book a Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
